// Global Instructions Rule Applied!
//
// CcBccEditor — Cc/Bcc copy-recipient picker, reused for both kinds. An AntD
// Select in "tags" mode so it looks and behaves like the multiselects used
// elsewhere in the app: selected recipients are pills INSIDE the box, you can
// pick a contact from the typeahead (two-line name + email option, per the
// distribution-lists §2.15f ruling) OR type ANY email address and press Enter
// (or comma/semicolon) to add a non-contact recipient.
//
// The persisted backend list is the source of truth: the Select value is
// derived from `items` via labelInValue, keyed by copy id — so removing a pill
// maps straight to onRemove(copyId), and adding fires onAdd with either a
// contact_id (option pick) or an email_address (free text). Because the value
// is controlled by `items`, an invalid or failed add never sticks.

import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Select } from 'antd'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type {
  EmailCampaignCopy,
  EmailCampaignCopyKind
} from '../../../state_manager/data_models/email_campaign_copy_model.ts'
import type { CopyContactOption } from '../services/emailService.ts'

// Mirror of the server-side regex (function_add_email_campaign_copy.sql) for a
// fast client pre-check; the RPC re-validates authoritatively.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
// Namespacing prefix so a picked contact option's value can't be confused with
// a persisted copy id (uuid) or a free-typed email.
const CONTACT_PREFIX = 'contact:'

interface LabeledValue {
  value: string
  label: React.ReactNode
}

interface OptionShape {
  value: string // `contact:<uuid>`
  label: string // contact display name (the pill text once selected)
  email: string
}

interface CcBccEditorProps {
  kind: EmailCampaignCopyKind
  items: EmailCampaignCopy[]
  disabled?: boolean
  onAdd: (
    kind: EmailCampaignCopyKind,
    payload: { contact_id?: string; email_address?: string }
  ) => Promise<string | null>
  onRemove: (copyId: string) => Promise<string | null>
  onSearchContacts: (term: string) => Promise<CopyContactOption[]>
}

export default function CcBccEditor({
  kind,
  items,
  disabled = false,
  onAdd,
  onRemove,
  onSearchContacts
}: CcBccEditorProps) {
  const label = kind === 'cc' ? 'Cc' : 'Bcc'
  const [options, setOptions] = useState<OptionShape[]>([])
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Controlled value = persisted copies, keyed by copy id. labelInValue lets us
  // show the contact name (or the email for manual entries) as the pill text.
  const value = useMemo<LabeledValue[]>(
    () => items.map((it) => ({ value: it.id, label: it.contact_name || it.email_address })),
    [items]
  )

  const runSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      const q = term.trim()
      if (!q) {
        setOptions([])
        return
      }
      debounceRef.current = setTimeout(async () => {
        const results = await onSearchContacts(q)
        setOptions(
          results.map((c) => ({ value: `${CONTACT_PREFIX}${c.value}`, label: c.label, email: c.email }))
        )
      }, 250)
    },
    [onSearchContacts]
  )

  // Diff the new selection against the persisted set: entries whose value isn't
  // an existing copy id are adds (contact pick or free email); persisted ids
  // missing from the new set are removes. Handles Enter, click, paste, and
  // comma/semicolon token separators uniformly.
  const handleChange = useCallback(
    async (next: LabeledValue[]) => {
      const currentIds = new Set(items.map((it) => it.id))
      const nextValues = new Set(next.map((n) => n.value))

      for (const it of items) {
        if (!nextValues.has(it.id)) {
          const err = await onRemove(it.id)
          if (err) showErrorToast(err)
        }
      }

      for (const n of next) {
        if (currentIds.has(n.value)) continue // existing persisted pill
        if (n.value.startsWith(CONTACT_PREFIX)) {
          const err = await onAdd(kind, { contact_id: n.value.slice(CONTACT_PREFIX.length) })
          if (err) showErrorToast(err)
        } else {
          const email = String(n.value).trim()
          if (!EMAIL_RE.test(email)) {
            showErrorToast(`"${email}" is not a valid email address`)
            continue
          }
          const err = await onAdd(kind, { email_address: email })
          if (err) showErrorToast(err)
        }
      }
      setOptions([])
    },
    [items, kind, onAdd, onRemove]
  )

  return (
    <Select<LabeledValue[]>
      mode='tags'
      labelInValue
      showSearch
      allowClear
      disabled={disabled}
      value={value}
      options={options}
      filterOption={false}
      onSearch={runSearch}
      onChange={handleChange}
      tokenSeparators={[',', ';']}
      notFoundContent={null}
      optionRender={(oi) => {
        const data = oi.data as unknown as OptionShape
        return (
          <div style={{ lineHeight: 1.25 }}>
            <div>{data.label}</div>
            {data.email ? (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{data.email}</div>
            ) : null}
          </div>
        )
      }}
      placeholder={`Add ${label}: search a contact, or type an email`}
      style={{ width: '100%' }}
    />
  )
}
